// ============================================================================
// optionsResolver — resolves `ref` field options_source to dropdown options
// ============================================================================
// Maps a `RefFieldSpec.options_source` value (e.g. 'mst.tbl_yard',
// 'admin.tbl_user_cache') to a fetcher that returns SelectOption[].
//
// The resolvers are deliberately small — each one calls an existing module
// service, maps the response to { value, label }, and returns. Adding a new
// master = adding one resolver entry. No new framework code.
//
// Until a master has a frontend service of its own, register a stub here
// that returns []. The DynamicForm degrades gracefully (empty dropdown with
// a placeholder hint).

import type { SelectOption } from 'api/types/formSpec.types';

export type OptionsResolver = (params?: { filter?: string; search?: string }) => Promise<SelectOption[]>;

const registry = new Map<string, OptionsResolver>();

/** Register a resolver for an `options_source` key. */
export function registerOptionsResolver(source: string, resolver: OptionsResolver): void {
  registry.set(source, resolver);
}

/** Resolve options for a `options_source`. Returns [] when no resolver registered. */
export async function resolveOptions(
  source: string,
  params?: { filter?: string; search?: string },
): Promise<SelectOption[]> {
  const resolver = registry.get(source);
  if (!resolver) {
    // eslint-disable-next-line no-console
    console.warn(`[optionsResolver] no resolver registered for "${source}" — returning empty list`);
    return [];
  }
  try {
    return await resolver(params);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`[optionsResolver] resolver "${source}" failed:`, err);
    return [];
  }
}

/** Check whether a resolver exists for a given source. */
export function hasResolver(source: string): boolean {
  return registry.has(source);
}
